import BaseSetup from '@/components/BaseSetup';
import { useOrganizationUserService } from '@/services/organization-user/OrganizationUserService';
import { Reactive } from '@/utils/decorators';
import { watch } from 'vue';

type QuickFilter = {
	type: 'all' | 'role__without' | 'tag__without'
	label: string
	count: number
};

export type SharedSelectUserListEmits = { (e: 'input', val: (number | undefined)[]): void };

export type SharedSelectUserListProps = {
	transferCreator?: boolean
	uniq?: boolean
	hideSearch?: boolean
	hideQuickFilters?: boolean
	title?: string
	subtitle?: string
	quickFiltersType?: 'role' | 'tag'
	value?: number[]
	excludeRoleId?: number
	excludeTagId?: number
	showHiddenUser?: boolean
};

export default class SharedSelectUserListSetup extends BaseSetup {

	static readonly defaultProps = {
		value: () => [],
		showHiddenUser: false,
	};

	private _modal?: OrionModal;

	@Reactive private readonly state = {
		total: 0,
		list: [] as IOrganizationUserListItemModel[],
		userSelected: [] as IOrganizationUserListItemModel[],
		activeQuickFilter: undefined as Undef<QuickFilter>,
		filter: {
			organizationId: undefined as number | undefined,
			organizationRoleIdToExclude: undefined as number | undefined,
			tagIdToExclude: undefined as number | undefined,
			showOnlyWithoutRole: false,
			search: undefined,
			showDisabled: false,
			noArmadoAccess: false,
			withHiddenUsers: false,
			showOnlyWithoutCompany: false,
			page: {
				index: 1,
				size: 20,
			},
			order: {
				property: 'Id',
				ascending: false,
			},
		} as IOrganizationUserPagedListParameters,
		quickFilters: [
			{
				type: 'all' as QuickFilter['type'],
				label: this.$t('user.all'),
				count: 0,
			},
			{
				type: 'role__without' as QuickFilter['type'],
				label: this.$t('user.withoutRole'),
				count: 0,
			},
			{
				type: 'tag__without' as QuickFilter['type'],
				label: this.$t('user.withoutTag'),
				count: 0,
			},
		] as QuickFilter[],
	};

	get userSelected () { return this.state.userSelected }
	get list () { return this.state.list }
	get total () { return this.state.total }
	get quickFiltersOptions (): QuickFilter[] { return this.state.quickFilters.filter(x => x.type === 'all' || x.type.split('__')[0] === this.props.quickFiltersType) }

	get search () { return this.state.filter.search }
	set search (val) { this.state.filter.search = val }

	get page () { return this.state.filter.page }
	set page (val) { this.state.filter.page = val }

	get filter () { return this.state.filter }
	set filter (val) { this.state.filter = val }

	getHiddenUserTooltip (user: IOrganizationUserListItemModel) {
		if (!user.user?.hasAccessToArmado && user.user?.isEnabledForSuite) return this.$t('user.noArmadoAccess');
		if (!user.user?.isEnabledForSuite && user.user?.hasAccessToArmado) return this.$t('user.userNotActiveOnSuite');
		if (!user.user?.isEnabledForSuite && !user.user?.hasAccessToArmado) return this.$t('user.userNotActiveAndNoArmadoAccess');
		return undefined;
	}

	constructor (
		private props: SharedSelectUserListProps,
		private emits: SharedSelectUserListEmits,
		_modal?: OrionModal,
	) {
		super();
		this._modal = _modal;

		// Initialize filter with props values
		this.state.filter.organizationId = this.self.organization!.id ?? undefined;
		this.state.filter.organizationRoleIdToExclude = this.props.excludeRoleId ?? undefined;
		this.state.filter.tagIdToExclude = this.props.excludeTagId ?? undefined;
		this.state.filter.withHiddenUsers = this.props.showHiddenUser ?? false;

		this.state.activeQuickFilter = this.state.quickFilters.first();

		watch(() => this.state.userSelected, (val: IOrganizationUserListItemModel[]) => {
			this.emits('input', val.mapKey('user').map((x: IUserModel | undefined) => x?.id));
		});

		watch(() => this.page.index, () => {
			this.refreshListAsync();
		});
	}

	protected async onBeforeMountAsync () {
		await this.refreshListAsync();
	}

	async refreshListAsync (resetPagination = false) {
		if (this.state.filter.organizationId) {
			if (resetPagination) this.state.filter.page.index = 1;

			this._modal?._loader()?.show(this.$t('common.loading'));
			const usersPagedList = await useOrganizationUserService().gridAsync(this.state.filter);
			this.state.list = usersPagedList.list;
			this.state.total = usersPagedList.count;
			this.state.quickFilters[0].count = usersPagedList.count;
			this._modal?._loader()?.hide();
		}
	}

	userIsHidden (user: IOrganizationUserListItemModel) {
		if (!this.self.suiteUserId) return false;
		return !!this.self.suiteUserId && (!user.user?.hasAccessToArmado || !user.user?.isEnabledForSuite);
	}

	addUsers () {
		this._modal?.trigger('add', [...this.userSelected]);
	}

	toggleSelectUser (user: IOrganizationUserListItemModel) {
		if (this.props.uniq) {
			this.state.userSelected = [user];
		}
		else {
			this.state.userSelected.toggle(user);
		}
	}

	selectFilter (filter: QuickFilter) {
		this.state.activeQuickFilter = filter;
		this.state.filter.showOnlyWithoutRole = filter.type === 'role__without';
		this.refreshListAsync(true);
	}

	filterIsSelected (filter: QuickFilter) {
		return this.state.activeQuickFilter?.type === filter.type;
	}

	userIsSelected (user: IOrganizationUserListItemModel) {
		return !!this.userSelected.findByKey(user.id);
	}

}
